import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import {
  PiRocketLaunchDuotone,
  PiYinYangDuotone,
  PiHandHeartDuotone,
} from "react-icons/pi";

const SLIDES = [
  {
    icon: PiRocketLaunchDuotone,
    title: "Uplift",
    description:
      "For the days where energy is already there. It builds on it, bringing momentum and a sense of direction.",
    bg: "bg-surface-card-green",
    text: "text-secondary-800",
    border: "border-secondary-800/15",
    tint: "from-white/40 to-transparent",
  },
  {
    icon: PiYinYangDuotone,
    title: "Balance",
    description:
      "For quieter, neutral days. It keeps things steady, clear, and grounded.",
    bg: "bg-surface-card-beige",
    text: "text-primary-800",
    border: "border-primary-800/15",
    tint: "from-white/40 to-transparent",
  },
  {
    icon: PiHandHeartDuotone,
    title: "Support",
    description:
      "For the days that feel heavier. It meets you with softness, reassurance, and care.",
    bg: "bg-surface-card-orange",
    text: "text-accent-800",
    border: "border-accent-800/15",
    tint: "from-white/40 to-transparent",
  },
];

export function MoodCardsSlider({ className }: { className?: string }) {
  const autoplay = useRef(
    Autoplay({ delay: 8000, stopOnInteraction: false, stopOnMouseEnter: true }),
  );
  const [emblaRef, embla] = useEmblaCarousel(
    { loop: true, align: "center", containScroll: "trimSnaps" },
    [autoplay.current],
  );
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    setSnaps(embla.scrollSnapList());
    embla.on("select", onSelect);
    embla.on("reInit", () => {
      setSnaps(embla.scrollSnapList());
      onSelect();
    });
    onSelect();
  }, [embla]);

  const scrollPrev = useCallback(() => embla?.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla?.scrollNext(), [embla]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") scrollPrev();
      if (e.key === "ArrowRight") scrollNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [scrollPrev, scrollNext]);

  const total = SLIDES.length;
  const active = SLIDES[selected] ?? SLIDES[0];

  return (
    <section
      className={className}
      aria-roledescription="carousel"
      aria-label="Mood tones"
    >
      <div className="mx-auto w-full max-w-[1322px]">
        <div
          className="relative overflow-hidden"
          ref={emblaRef}
          style={{ willChange: "transform" }}
        >
          <div className="flex items-stretch">
            {SLIDES.map((slide, i) => {
              const Icon = slide.icon;
              const number = String(i + 1).padStart(2, "0");
              return (
                <div
                  key={i}
                  className="min-w-0 shrink-0 grow-0 basis-full px-3 md:px-4"
                >
                  <article
                    className={`relative flex h-full flex-col rounded-[28px] border ${slide.border} ${slide.bg} bg-gradient-to-b ${slide.tint} px-7 py-9 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_40px_-24px_rgba(0,0,0,0.14)] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:px-11 md:py-12`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <Icon
                        className={`h-8 w-8 shrink-0 ${slide.text}`}
                        aria-hidden="true"
                      />
                      <span
                        className={`font-sans text-[11px] font-medium uppercase tracking-[0.22em] ${slide.text} opacity-60`}
                      >
                        TONE #{number}
                      </span>
                    </div>

                    <div className="mt-10 flex flex-col gap-4 md:mt-14">
                      <p
                        className={`font-display text-[30px] font-semibold leading-[1.15] tracking-[-0.01em] ${slide.text} md:text-[38px]`}
                      >
                        {slide.title}
                      </p>
                      <p
                        className={`max-w-[560px] font-sans text-[15px] font-normal leading-[1.7] ${slide.text} opacity-80`}
                      >
                        {slide.description}
                      </p>
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>

        {total > 1 && (
          <div className="mt-6 flex items-center justify-end px-2 md:px-4">
            <div className="flex items-center gap-3">
              <span
                aria-live="polite"
                className="font-sans text-[11px] font-medium uppercase tracking-[0.22em] text-text-muted"
              >
                {String(selected + 1).padStart(2, "0")}{" "}
                <span className="opacity-40">/ {String(total).padStart(2, "0")}</span>
                <span className="sr-only"> — {active.title}</span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={scrollPrev}
                  aria-label="Previous mood"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 text-text-primary transition-colors hover:border-primary-600 hover:bg-primary-600 hover:text-[var(--background-main)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background-main)]"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={scrollNext}
                  aria-label="Next mood"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 text-text-primary transition-colors hover:border-primary-600 hover:bg-primary-600 hover:text-[var(--background-main)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background-main)]"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
